// Puts you at the TOP of a page you just navigated to.
//
// Without this, React Router leaves the scroll position exactly where it was:
// clicking "About Us" from the FOOTER — the only place those links live — kept
// you scrolled to the bottom, so the new page opened halfway down and looked
// broken. A browser does this for free on a real page load; a SPA has to do it
// by hand.
//
// Mounted inside PublicLayout, not at the router root, so it governs the public
// site only. /dashboard/* swaps sections under a splat route and owns its own
// scrolling; nothing here should reach into it.

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/** .nav is position:fixed and 68px tall at EVERY breakpoint — see theme.css. */
const NAV_H = 68;

export default function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    // A hash means the link is aiming at a SECTION, not the page top — the
    // footer's /#awards and /#how-it-works are the two that do it. Handling
    // them here is also what makes them work at all: React Router navigates
    // with pushState, which (unlike a real fragment navigation) does NOT scroll
    // the browser to the target, so those two links used to change the URL and
    // move nothing.
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        // Land the section BELOW the fixed header rather than under it, which
        // is what a bare scrollIntoView() would do.
        const y = el.getBoundingClientRect().top + window.scrollY - NAV_H;
        window.scrollTo({ top: y, behavior: 'smooth' });
        return;
      }
      // Hash with no matching element: fall through to the top, so a stale
      // link still lands somewhere sensible.
    }

    // `instant` deliberately overrides the global `html{scroll-behavior:smooth}`
    // from theme.css. Animating a whole page back to the top on every route
    // change reads as lag, not polish — and on a long page it means watching
    // the old content fly past before the new page settles.
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname, hash]);

  return null;
}
